import React, { useState, useEffect } from "react";
import { Col, Container, Row, Button, Alert } from "react-bootstrap";

import "../../Style.css";
import List from "../../components/listing/List";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import { FaSearch, FaTimes } from "react-icons/fa";
import Api from "../../components/api/Api";
import { useSelector } from "react-redux";
import axios from "axios";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import Tr from "../../components/tr/Tr";

const PartnerLeadList = () => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const getLeadsApi = Api({
    url: "admin/service/leads/all",
  });

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const columns = [
    { displayName: "S.No.", render: (item, index) => index + 1 },
    { displayName: "First Name", key: "first_name" },
    { displayName: "Last Name", key: "last_name" },
    { displayName: "Mobile", key: "mobile_number" },
    { displayName: "Partner", render: (item) => item.associated_partner?.name || "N/A" },
    { 
      displayName: "Status", 
      render: (item) => (
        <select 
          className={`form-select form-select-sm text-white ${item.status === 'COMPLETED' ? 'bg-success' : item.status === 'REJECTED' ? 'bg-danger' : item.status === 'INPROGRESS' ? 'bg-warning text-dark' : 'bg-secondary'}`}
          value={item.status || "PENDING"} 
          onChange={(e) => handleStatusChange(item.id, e.target.value)}
          style={{ cursor: "pointer", width: "130px", fontWeight: "600", fontSize: "0.85rem", border: "none" }}
        >
          <option value="PENDING" className="bg-light text-dark">PENDING</option>
          <option value="INPROGRESS" className="bg-light text-dark">INPROGRESS</option>
          <option value="COMPLETED" className="bg-light text-dark">COMPLETED</option>
          <option value="REJECTED" className="bg-light text-dark">REJECTED</option>
        </select>
      ) 
    },
  ];

  // Simple debounce logic for search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchLeadsData = async (pageNumber = 1, keyword = "") => {
    try {
      const payload = {
        page_number: pageNumber,
        page_size: 10,
        order_by: "ASC",
        keyword_search: keyword
      };
      const responseData = await getLeadsApi.postData(payload);

      let fetchedData = [];
      let total = 1;

      if (responseData && responseData.data) {
        if (responseData.data.serviceLeads) {
          fetchedData = responseData.data.serviceLeads;
        } else if (responseData.data.serviceLead) {
          fetchedData = Array.isArray(responseData.data.serviceLead) ? responseData.data.serviceLead : [responseData.data.serviceLead];
        }
        total = responseData.data.totalPage || 1;
      }

      setData(fetchedData || []);
      setTotalPages(total);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchLeadsData(currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteAlertClose = () => {
    setShowDeleteAlert(false);
    setDeleteId(null);
  };

  const handleDeleteAlertShow = (id) => {
    setDeleteId(id);
    setShowDeleteAlert(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response = await axios.delete(
        `${baseUrl}/admin/service/lead/delete/${deleteId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.statusType === "SUCCESS") {
        setData(data.filter((item) => item.id !== deleteId));
        showToast("Partner Lead deleted successfully", { type: "success" });
      } else {
        console.error("Error deleting item:", response.status);
        showToast("Failed to delete Partner Lead", { type: "error" });
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      showToast("Error Occurred", { type: "error" });
    } finally {
      handleDeleteAlertClose();
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const response = await axios.put(
        `${baseUrl}/admin/service/status`,
        { lead_id: leadId, status: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.statusType === "SUCCESS") {
        showToast("Status updated successfully", { type: "success" });
        setData((prevData) =>
          prevData.map((item) =>
            item.id === leadId ? { ...item, status: newStatus } : item
          )
        );
      } else {
        showToast(response.data.text || "Failed to update status", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Error updating status", { type: "error" });
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const initialValues = {};
  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Partner Network", link: "/partners" },
    { label: "Partner Leads", link: "/partner-leads", active: true },
  ];

  return (
    <>
      <Notification />
      <div className="content-body2">
        <Container>
          <BreadCrumb items={breadcrumbItems} />

          <Alert
            show={showDeleteAlert}
            variant="danger"
            onClose={handleDeleteAlertClose}
            dismissible
          >
            <Alert.Heading>Confirm Delete</Alert.Heading>
            <p>Are you sure you want to delete this partner lead?</p>
            <div className="d-flex justify-content-end">
              <Button
                onClick={handleDeleteAlertClose}
                variant="outline-danger"
                className="me-3"
              >
                Cancel
              </Button>
              <Button onClick={handleDelete} variant="danger">
                Delete
              </Button>
            </div>
          </Alert>

          <Row className="mb-3 mt-2 justify-content-end">
            <Col md={4} className="d-flex justify-content-end">
              {!showSearch ? (
                <Button
                  onClick={() => setShowSearch(true)}
                  className="d-flex align-items-center"
                  style={{ backgroundColor: "#1971C2", borderColor: "#1971C2", color: "#fff" }}
                >
                  <FaSearch className="me-2" /> Search
                </Button>
              ) : (
                <div className="d-flex w-100 align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                  <Button
                    variant="outline-danger"
                    className="ms-2 d-flex align-items-center justify-content-center"
                    onClick={() => {
                      setShowSearch(false);
                      setSearchTerm("");
                    }}
                    style={{ padding: "8px 12px" }}
                  >
                    <FaTimes />
                  </Button>
                </div>
              )}
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} sm={12}>
              {data && data.length === 0 ? (
                <Tr
                  columns={columns}
                  initialValues={initialValues}
                  title="No Partner Leads Found"
                  buttonName="Create"
                  btnLink="/partner-leads/add"
                  titleHeader="Partner Leads"
                />
              ) : (
                <List
                  title="Partner Leads"
                  data={data}
                  columns={columns}
                  button="Create"
                  route="partner-leads"
                  to="add"
                  handleDelete={handleDeleteAlertShow}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default PartnerLeadList;
